/**
 * Wrapper de `fetch` que SIEMPRE inyecta los headers de tenant
 * (X-Tenant cuando hay subdominio o el slug guardado en localStorage)
 * y el token de autenticación.
 *
 * REGLA: todo el código del frontend DEBE usar `apiFetch` en vez
 * de `fetch` directo para llamadas al backend. Esto es crítico
 * para multi-tenancy: si el header X-Tenant no se manda, el
 * backend no puede resolver el tenant y las queries caen al
 * platformModel (compartido por todos los tenants).
 *
 * Si la URL es a un endpoint "platform" (/auth/login, /tenants/*,
 * /auth/tenant-resolve, etc.) el helper no agrega X-Tenant
 * automáticamente.
 */
import { tenantHeaders } from './tenantHeaders';

// Endpoints que NO requieren tenant (operan en modo plataforma).
const PLATFORM_PREFIXES = [
  '/api/auth/login',
  '/api/auth/tenant-resolve',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/tenants',
  // Endpoints públicos (trazabilidad del cliente, etc.).
  // El cliente entra con NIT + número, sin estar logueado.
  '/api/public',
];

function isPlatformEndpoint(url) {
  if (!url) return false;
  return PLATFORM_PREFIXES.some((p) => url.startsWith(p));
}

/**
 * Equivalente a `fetch` pero con headers de tenant + auth inyectados.
 *
 * @param {string} url - URL completa o relativa.
 * @param {RequestInit} options - Mismas opciones que `fetch`.
 * @returns {Promise<Response>}
 */
export function apiFetch(url, options = {}) {
  const headers = isPlatformEndpoint(url)
    ? { ...(options.headers || {}) }
    : tenantHeaders(options.headers || {});
  return fetch(url, { ...options, headers });
}

export default apiFetch;
